import { useEffect, useState } from 'react'

type ApiResourceLoader<T> = () => Promise<T>

type ApiResourceResult<T> = {
  load: ApiResourceLoader<T>
  data: T | null
  error: string | null
}

export type ApiResource<T> = {
  data: T | null
  error: string | null
  isLoading: boolean
}

/**
 * Runs `load` each time its identity changes and keeps only the latest result.
 * Wrap `load` in `useCallback` with the request inputs as dependencies.
 * Pass `null` to skip the request.
 */
export const useApiResource = <T>(
  load: ApiResourceLoader<T> | null,
  fallbackError: string
): ApiResource<T> => {
  const [result, setResult] = useState<ApiResourceResult<T> | null>(null)

  useEffect(() => {
    if (!load) {
      return
    }

    let isCurrent = true

    load().then(
      (data) => {
        if (isCurrent) {
          setResult({ load, data, error: null })
        }
      },
      (loadError: unknown) => {
        if (isCurrent) {
          setResult({
            load,
            data: null,
            error: loadError instanceof Error ? loadError.message : fallbackError,
          })
        }
      }
    )

    return () => {
      isCurrent = false
    }
  }, [load, fallbackError])

  if (!load) {
    return { data: null, error: null, isLoading: false }
  }

  if (result?.load !== load) {
    return { data: null, error: null, isLoading: true }
  }

  return { data: result.data, error: result.error, isLoading: false }
}
